import { Fragment, useState, type ReactNode } from 'react';
import { Clock, Ban, MailX } from 'lucide-react';
import type { Email, ThreadMessage, ContactApprovalState } from '@/types/email';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SecurityBadges } from './SecurityBadges';
import { SpamScoreBadge } from '@/components/SpamScoreBadge';
import { formatDate } from '@/features/email-list/utils/emailFormatting';
import { EmailTaskCreationIconButton } from './EmailTaskCreation';
import { useSenderProfileStore } from '@/stores/senderProfileStore';
import { EmailEventCreationIconButton } from './EmailEventCreation';
import { EmailProductLineCreationIconButton } from './EmailProductLineCreation';
import { ActionButtons } from './ActionButtons';

type EmailLike = Email | ThreadMessage;
export type ReplyMode = 'reply' | 'replyAll' | 'forward';
export { ActionButtons } from './ActionButtons';

interface AddressEntry {
  label: string;
  value: string;
}

interface MessageMetadataProps {
  email: EmailLike;
  variant: 'standalone' | 'embedded';
  align: 'start' | 'end';
  senderAvatarSrc: string | null;
  addressTooltipEntries: AddressEntry[];
  messageAddressEntries: AddressEntry[];
  onArchive?: (emailId: string) => void;
  onDelete?: (emailId: string) => void;
  onReply?: (mode: ReplyMode) => void;
  isParticipantContext: boolean;
  contactApprovalState: ContactApprovalState | null;
  contactBadgeVariant: 'default' | 'secondary' | 'destructive';
  hasPgp: boolean;
  hasSmime: boolean;
  onSummarize?: () => void;
  onRehydrate?: (emailId: string) => void;
  rehydrating?: boolean;
  unsubscribeUrl?: string | null;
  onAddSenderLabel?: () => void;
  onViewOcrData?: () => void;
  onBlockSender?: () => void;
  onMoveToFolder?: () => void;
  onMarkRead?: (emailId: string, read: boolean) => void;
  onCopyAiPayload?: () => void;
  // Training props (optional)
  canTrainSpam?: boolean;
  canTrainGood?: boolean;
  isTrainingSpam?: boolean;
  isTrainingGood?: boolean;
  onTrainSpam?: () => void;
  onTrainGood?: () => void;
}

export function MessageMetadata({
  email,
  variant,
  align,
  senderAvatarSrc,
  addressTooltipEntries,
  messageAddressEntries,
  onArchive,
  onDelete,
  onReply,
  isParticipantContext,
  contactApprovalState,
  hasPgp,
  hasSmime,
  onSummarize,
  onRehydrate,
  rehydrating,
  unsubscribeUrl,
  onAddSenderLabel,
  onViewOcrData,
  onBlockSender,
  onMoveToFolder,
  onMarkRead,
  onCopyAiPayload,
  canTrainSpam,
  canTrainGood,
  isTrainingSpam,
  isTrainingGood,
  onTrainSpam,
  onTrainGood,
}: MessageMetadataProps) {
  if (variant === 'embedded') {
    // One standard action row shared by both embedded layouts — same grammar
    // as the detail header and every other desktop reading surface.
    const actions = (
      <ActionButtons
        emailId={email.id}
        onArchive={onArchive!}
        onDelete={onDelete!}
        onReply={onReply}
        isRead={email.read}
        onMarkRead={onMarkRead}
        onSummarize={onSummarize}
        onRehydrate={onRehydrate}
        rehydrating={rehydrating}
        extraActions={<><EmailTaskCreationIconButton /><EmailEventCreationIconButton /><EmailProductLineCreationIconButton /></>}
        onAddLabel={onAddSenderLabel}
        onCopyAiPayload={onCopyAiPayload}
        onViewOcrData={onViewOcrData}
        onBlockSender={onBlockSender}
        onMoveToFolder={onMoveToFolder}
        canTrainSpam={canTrainSpam}
        canTrainGood={canTrainGood}
        isTrainingSpam={isTrainingSpam}
        isTrainingGood={isTrainingGood}
        onTrainSpam={onTrainSpam}
        onTrainGood={onTrainGood}
      />
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <header
            className={cn(
              isParticipantContext
                ? 'space-y-2 rounded-lg bg-accent/40 p-2 glass'
                : 'flex flex-col gap-4 md:flex-row md:items-start md:justify-between',
              align === 'end' && !isParticipantContext && 'md:flex-row-reverse',
            )}
          >
            {isParticipantContext ? (
              <ParticipantMetadata
                email={email}
                align={align}
                contactApprovalState={contactApprovalState}
                hasPgp={hasPgp}
                hasSmime={hasSmime}
                actions={actions}
              />
            ) : (
              <CompactMetadata
                email={email}
                align={align}
                senderAvatarSrc={senderAvatarSrc}
                messageAddressEntries={messageAddressEntries}
                hasPgp={hasPgp}
                hasSmime={hasSmime}
                unsubscribeUrl={unsubscribeUrl}
                actions={actions}
              />
            )}
          </header>
        </TooltipTrigger>
        {addressTooltipEntries.length > 0 && (
          <TooltipContent
            side="top"
            align={align === 'end' ? 'end' : 'start'}
            className="max-w-sm space-y-1 text-xs leading-relaxed"
          >
            {addressTooltipEntries.map((entry) => (
              <div key={entry.label}>
                <span className="font-medium">{entry.label}:</span>{' '}
                <span className="text-muted">{entry.value}</span>
              </div>
            ))}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  const toEntry = messageAddressEntries.find((entry) => entry.label === 'To');
  const ccEntry = messageAddressEntries.find((entry) => entry.label === 'Cc');
  const fromName = email.from?.name;
  const fromEmail = email.from?.email ?? '';

  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <Avatar className="h-12 w-12 ring-1 ring-border">
          {senderAvatarSrc && (
            <AvatarImage
              src={senderAvatarSrc}
              alt={fromName || fromEmail || 'Sender avatar'}
            />
          )}
          <AvatarFallback>{getInitials(fromName ?? undefined, fromEmail)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <SenderEmailDisplay name={fromName ?? undefined} email={fromEmail} />
              {unsubscribeUrl && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild size="sm" variant="ghost" className="-ml-1">
                      <a href={unsubscribeUrl} target="_blank" rel="noopener noreferrer">
                        <MailX className="h-4 w-4" />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Unsubscribe
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <DateDisplay date={email.date} />
          </div>
          {(toEntry || ccEntry) && (
            <div className="space-y-0.5 text-xs text-muted-foreground">
              {toEntry && (
                <div>
                  <span className="font-medium text-foreground">To:</span> {toEntry.value}
                </div>
              )}
              {ccEntry && (
                <div>
                  <span className="font-medium text-foreground">Cc:</span> {ccEntry.value}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function ParticipantMetadata({
  email,
  align,
  contactApprovalState,
  hasPgp,
  hasSmime,
  actions,
}: {
  email: EmailLike;
  align: 'start' | 'end';
  contactApprovalState: ContactApprovalState | null;
  hasPgp: boolean;
  hasSmime: boolean;
  actions: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-2', align === 'end' && 'items-end text-right')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{email.subject || 'No subject'}</span>
        {contactApprovalState && contactApprovalState !== 'approved' && (
          <Tooltip>
            <TooltipTrigger asChild>
              {contactApprovalState === 'blocked' ? (
                <Ban className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
              ) : (
                <Clock className="h-3.5 w-3.5 flex-shrink-0 text-warning-ink" />
              )}
            </TooltipTrigger>
            <TooltipContent>
              {contactApprovalState === 'blocked' ? 'Blocked sender' : 'Pending approval'}
            </TooltipContent>
          </Tooltip>
        )}
        {!email.read && <Badge variant="default">Unread</Badge>}
        <SecurityBadges hasPgp={hasPgp} hasSmime={hasSmime} spamReport={email.spamReport} headers={email.headers} />
        {email.spamScore !== null && email.spamScore !== undefined && (
          <SpamScoreBadge
            score={email.spamScore}
            requiredScore={email.spamRequiredScore}
            action={email.spamAction}
            report={email.spamReport}
            scannedAt={email.spamScannedAt}
            size="sm"
          />
        )}
      </div>
      <div className="flex w-full flex-wrap items-center gap-2 justify-between">
        <span className={cn('text-xs text-muted-foreground', align === 'end' && 'text-right')}>
          {email.date.toLocaleString()}
        </span>
        {actions}
      </div>
    </div>
  );
}

function CompactMetadata({
  email,
  align,
  senderAvatarSrc,
  messageAddressEntries,
  hasPgp,
  hasSmime,
  unsubscribeUrl,
  actions,
}: {
  email: EmailLike;
  align: 'start' | 'end';
  senderAvatarSrc: string | null;
  messageAddressEntries: AddressEntry[];
  hasPgp: boolean;
  hasSmime: boolean;
  unsubscribeUrl?: string | null;
  actions: ReactNode;
}) {
  const fromName = email.from?.name;
  const fromEmail = email.from?.email ?? '';

  return (
    <Fragment>
      <div className={cn('flex items-start gap-3', align === 'end' && 'flex-row-reverse')}>
        <Avatar className="h-10 w-10 ring-1 ring-border">
          {senderAvatarSrc && (
            <AvatarImage
              src={senderAvatarSrc}
              alt={fromName || fromEmail || 'Sender avatar'}
            />
          )}
          <AvatarFallback>{getInitials(fromName ?? undefined, fromEmail)}</AvatarFallback>
        </Avatar>
        <div className={cn('space-y-1', align === 'end' && 'text-right')}>
          <div className="flex flex-wrap items-center gap-2">
            <SenderEmailDisplay name={fromName ?? undefined} email={fromEmail} className="text-sm text-foreground" />
            {unsubscribeUrl && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild size="icon" variant="ghost" className="h-6 w-6">
                    <a href={unsubscribeUrl} target="_blank" rel="noopener noreferrer">
                      <MailX className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Unsubscribe
                </TooltipContent>
              </Tooltip>
            )}
            <SecurityBadges hasPgp={hasPgp} hasSmime={hasSmime} spamReport={email.spamReport} headers={email.headers} />
            {email.spamScore !== null && email.spamScore !== undefined && (
              <SpamScoreBadge
                score={email.spamScore}
                requiredScore={email.spamRequiredScore}
                action={email.spamAction}
                report={email.spamReport}
                scannedAt={email.spamScannedAt}
                size="sm"
              />
            )}
          </div>
          <DateDisplay date={email.date} className="text-xs text-muted-foreground" />
          {messageAddressEntries.length > 0 && (
            <div className="space-y-0.5 text-xs text-muted-foreground">
              {messageAddressEntries.map((entry) => (
                <div key={entry.label}>
                  <span className="font-medium text-foreground">{entry.label}:</span> {entry.value}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={cn('flex flex-wrap items-center gap-2', align === 'end' && 'md:flex-row-reverse md:justify-end')}>
        {actions}
      </div>
    </Fragment>
  );
}

function DateDisplay({ date, className }: { date: Date | string; className?: string }) {
  const [showFullDate, setShowFullDate] = useState(false);
  const emailDate = date instanceof Date ? date : new Date(date);
  const fullDate = emailDate.toLocaleString(undefined, {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  return (
    <div className={className}>
      {/* Mobile: show short date, tap to toggle full date */}
      <button
        type="button"
        onClick={() => setShowFullDate(!showFullDate)}
        className="md:hidden text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {showFullDate ? fullDate : formatDate(date)}
      </button>
      {/* Desktop: show short date with tooltip */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="hidden md:inline text-xs text-muted-foreground">{formatDate(date)}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{fullDate}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function SenderEmailDisplay({
  name,
  email,
  className,
}: {
  name?: string;
  email?: string;
  className?: string;
}) {
  const [showEmail, setShowEmail] = useState(false);
  const displayName = name || email || '';
  const shouldShowEmailToggle = name && email && name !== email;

  const openProfile = () => {
    if (email) {
      useSenderProfileStore.getState().openProfile(email);
    }
  };

  if (!shouldShowEmailToggle) {
    if (!email) {
      return <span className={className}>{displayName}</span>;
    }
    return (
      <button
        type="button"
        onClick={openProfile}
        className={cn('text-left hover:text-foreground hover:underline transition-colors', className)}
        title="Sender profile"
      >
        {displayName}
      </button>
    );
  }

  return (
    <>
      {/* Mobile: show name, tap to toggle email (long-standing behavior);
          the profile lives behind the desktop click and the list context menu */}
      <button
        type="button"
        onClick={() => setShowEmail(!showEmail)}
        className={cn('md:hidden text-left font-semibold hover:text-foreground transition-colors', className)}
      >
        {showEmail ? email : name}
      </button>
      {/* Desktop: click opens the sender profile; tooltip still shows the address */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={openProfile}
            className={cn('hidden md:inline text-left font-semibold hover:underline', className)}
          >
            {name}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{email}</p>
        </TooltipContent>
      </Tooltip>
    </>
  );
}

function getInitials(name?: string, email?: string) {
  if (name) {
    const initials = name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase())
      .join('');
    if (initials) {
      return initials.slice(0, 2);
    }
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'EM';
}
